import type { Recipe } from '../types';

// Replaces the sample recipe. Corrected to match what was actually bought:
// avocado oil (not vegetable), light brown sugar, and a sear rather than a
// shallow-fry — a cup of $16 avocado oil per batch is not a real option.

export const mongolianBeef: Recipe = {
  slug: 'mongolian-beef',
  title: 'Mongolian Beef',
  meal: 'dinner',
  category: 'meal',
  types: ['stir-fry', 'rice-bowl'],
  cuisines: ['chinese'],
  traits: ['freezer-friendly', 'takeout-style'],
  yield: '8 servings',
  activeTime: '45 min',
  totalTime: '1 hr',
  freezerLife: '2 months',
  difficulty: 2,

  summary:
    'Thin-sliced beef in a sweet soy glaze with green onions. Freezes better than almost anything on this list — the beef sits in the sauce and keeps tenderizing, so week three is better than day one.',

  flavorNotes:
    'Sweet and savory, not spicy — there is no heat in this at all. The sauce should coat a spoon like thin syrup and cling to the beef rather than pooling in the pan. The beef should be silky and yielding, never chewy, and that texture comes entirely from velveting, not from expensive meat. If you have only had this from a takeout container, the homemade version is less gloopy and less sweet, which is the point.',

  ingredients: [
    {
      id: 'sirloin',
      name: 'Sirloin',
      total: '2 lb',
      prep: 'sliced thin against the grain, about ¼ inch',
      fromFreezer: true,
      uses: [{ step: 1, amount: '2 lb' }],
    },
    {
      id: 'baking-soda',
      name: 'Baking soda',
      total: '1 tsp',
      uses: [{ step: 1, amount: '1 tsp', note: 'velveting — rinsed off after' }],
    },
    {
      id: 'soy',
      name: 'Low-sodium soy sauce',
      total: '1 cup + 1 tbsp',
      uses: [
        { step: 1, amount: '1 tbsp', note: 'velveting marinade' },
        { step: 4, amount: '1 cup', note: 'the sauce' },
      ],
    },
    {
      id: 'cornstarch',
      name: 'Cornstarch',
      total: '½ cup',
      uses: [{ step: 2, amount: '½ cup', note: 'coats the beef' }],
    },
    {
      id: 'avocado-oil',
      name: 'Avocado oil',
      total: '¼ cup + 1 tbsp',
      uses: [
        { step: 3, amount: '¼ cup', note: 'searing the beef, in batches' },
        { step: 4, amount: '1 tbsp', note: 'the garlic and ginger' },
      ],
    },
    {
      id: 'garlic',
      name: 'Garlic',
      total: '8 cloves',
      prep: 'minced',
      uses: [{ step: 4, amount: '8 cloves' }],
    },
    {
      id: 'ginger',
      name: 'Fresh ginger',
      total: '2 tbsp',
      prep: 'peeled and grated fine',
      uses: [{ step: 4, amount: '2 tbsp' }],
    },
    {
      id: 'brown-sugar',
      name: 'Light brown sugar',
      total: '1 cup, packed',
      uses: [{ step: 4, amount: '1 cup' }],
    },
    {
      id: 'water',
      name: 'Water',
      total: '1 cup',
      uses: [{ step: 4, amount: '1 cup' }],
    },
    {
      id: 'carrot',
      name: 'Carrot',
      total: '1 large',
      prep: 'grated as fine as you can get it',
      uses: [{ step: 4, amount: '1 large', note: 'disappears into the sauce' }],
    },
    {
      id: 'green-onions',
      name: 'Green onions',
      total: '2 bunches',
      prep: 'cut into 2-inch pieces',
      uses: [{ step: 6, amount: '2 bunches' }],
    },
    {
      id: 'sesame-oil',
      name: 'Toasted sesame oil',
      total: '1 tsp',
      uses: [{ step: 6, amount: '1 tsp', note: 'off the heat, at the very end' }],
    },
    {
      id: 'sesame-seeds',
      name: 'Sesame seeds',
      total: 'a small handful',
      optional: true,
      optionalKind: 'garnish',
      optionalWhy:
        'Purely visual, plus a faint crunch. No flavor consequence either way.',
      uses: [{ step: 6, amount: 'a pinch per bowl', note: 'at the table' }],
    },
    {
      id: 'rice',
      name: 'Jasmine rice',
      total: '3 cups dry',
      optional: true,
      optionalKind: 'serve-with',
      optionalWhy:
        'Cook a big batch and freeze it in its own portioned bags — about 80% as good as fresh and worth it on a weeknight. Just never freeze it sitting in the glaze.',
      uses: [{ step: 7, amount: '3 cups dry', note: 'frozen in its own bags, never in with the beef' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Velvet the beef',
      time: '15 min',
      critical: true,
      ingredientIds: ['sirloin', 'baking-soda', 'soy'],
      body:
        'Slice the sirloin as thin as you can manage, cutting against the grain. If it is still half-frozen this is much easier — firm, slightly icy beef slices paper-thin where fully thawed beef squashes and tears. Toss the slices with {1 tsp|baking-soda} of baking soda, {1 tablespoon} of water, and {1 tablespoon|soy} of soy sauce until every piece is coated — this is velveting. Let it sit 15 minutes, no longer than 20. Then rinse it thoroughly in a colander under cold water and pat it very dry with paper towels.',
      tip:
        'Rinse it properly. The baking soda has already done its work by then, and any left behind tastes faintly of soap.',
    },
    {
      n: 2,
      title: 'Coat the beef',
      time: '5 min',
      ingredientIds: ['cornstarch'],
      body:
        'Toss the dried beef with the cornstarch until every piece carries a thin, even dusting. Shake off the excess — you want a coating you can barely see, not a crust of dry powder. Let it sit while you get the pan hot; the coating goes slightly tacky, which is what helps it brown.',
    },
    {
      n: 3,
      title: 'Sear the beef in batches',
      time: '15 min',
      critical: true,
      ingredientIds: ['avocado-oil'],
      body:
        'Slick the pan with just enough avocado oil to coat it — about {1 tablespoon} to start — and heat a large skillet or wok over medium-high until it shimmers. Lay in a single layer of beef with space between the pieces and leave it alone for about a minute, then flip and give it another 30 seconds. Move it to a plate and repeat with the rest, adding a little more oil between batches; three or four batches will use the {¼ cup|avocado-oil} set aside for searing. The beef does not need to be cooked through here; it finishes in the sauce.',
      tip:
        'Crowding the pan is the one mistake that ruins this. Too much beef at once drops the temperature and the meat steams grey instead of searing brown. Smaller batches, every time.',
    },
    {
      n: 4,
      title: 'Build the sauce',
      time: '8 min',
      ingredientIds: ['avocado-oil', 'garlic', 'ginger', 'soy', 'water', 'brown-sugar', 'carrot'],
      body:
        'Turn the heat to medium and add the remaining {1 tablespoon|avocado-oil} of oil to the same pan. Add the garlic and ginger and stir for about 30 seconds — just until fragrant, and do not let the garlic take on color or it turns bitter. Add the soy sauce, water, brown sugar, and the grated carrot. Bring it to a gentle boil, then simmer 3 to 4 minutes until the sugar has fully dissolved and the sauce looks glossy.',
      tip:
        'The carrot melts into the sauce completely — no texture, no color, no clue. It is the easiest hidden vegetable in this whole book.',
    },
    {
      n: 5,
      title: 'Glaze',
      time: '4 min',
      ingredientIds: [],
      body:
        'Return all the beef and any juices on the plate to the pan. Simmer, stirring, for 2 to 3 minutes until the sauce thickens enough to coat a spoon and cling to the meat. Stop a little earlier than feels right — it keeps thickening as it cools, and it thickens again in the freezer.',
    },
    {
      n: 6,
      title: 'Finish off the heat',
      time: '2 min',
      ingredientIds: ['green-onions', 'sesame-oil', 'sesame-seeds'],
      body:
        'Kill the heat. Stir in the green onions and the sesame oil — the residual heat softens the onions without cooking the freshness out of them, and sesame oil loses its aroma if you actually cook it. Sesame seeds go over individual bowls at the table.',
    },
    {
      n: 7,
      title: 'Serve now, or cool and pack',
      time: '30 min, mostly waiting',
      ingredientIds: ['rice'],
      body:
        'Cook the full {3 cups dry|rice} of jasmine rice regardless of how much beef you are eating tonight — it freezes well on its own and you will want it. Spread the beef on a sheet pan and let it cool completely, about 30 minutes; packing it warm makes ice crystals and mushy beef. Portion the beef into quart freezer bags, press flat, push the air out, and label. Bag the rice separately in single-meal portions and freeze those alongside.',
      tip:
        'Two different things get confused here. Rice frozen sitting in the glaze turns to paste — that genuinely does not work. Rice frozen in its own bag comes back at about 80% of fresh, which on a Tuesday is the right trade. Cool it fast and get it in the freezer promptly rather than leaving it out on the counter to come down slowly.',
    },
  ],

  freezing:
    'Freeze flat in quart bags with the air pressed out, up to 2 months. Rice freezes too, but in its own bags — beef in one, rice in another, reheated separately and combined on the plate. This is the one dish here that genuinely improves in the freezer: the beef sits in the glaze for weeks and keeps tenderizing.',

  reheat: [
    {
      method: 'Skillet',
      best: true,
      detail:
        'Thaw overnight in the fridge. Empty the bag into a skillet with a splash of water, cover, and warm over medium-low, stirring once or twice, about 5 minutes. Best texture by a wide margin — the glaze re-tightens as it heats.',
    },
    {
      method: 'Microwave',
      detail:
        'Thaw overnight, then microwave covered at 70% power in 90-second rounds, stirring between. Full power overcooks the thin edges of the beef before the middle is hot.',
    },
    {
      method: 'From frozen',
      detail:
        'In a pinch: run the sealed bag under warm water for a minute to release the block, then into a covered skillet on low with 2 tablespoons of water for about 10 minutes, stirring gently as it loosens.',
    },
    {
      method: 'Frozen rice',
      detail:
        'Straight from the freezer, no thawing. Microwave the bag’s worth covered with a splash of water, about 2 minutes for a single portion, then fluff it with a fork. The splash of water is the whole trick — it re-steams the grains instead of drying them out.',
    },
  ],

  glossary: [
    {
      term: 'velveting',
      plain:
        'A short baking-soda soak that raises the pH on the surface of the meat so the proteins do not seize and squeeze out water when they hit heat. The beef stays silky instead of turning tight and chewy. This is the actual secret behind takeout beef texture — not the cut, not the oil.',
    },
    {
      term: 'against the grain',
      plain:
        'Look for the fine lines running in one direction through the meat. Cut across them, not alongside them. Cutting across shortens the muscle fibers so they give way easily when you chew.',
    },
    {
      term: 'coat a spoon',
      plain:
        'Dip a spoon in and lift it out. The sauce should cling in a thin, even layer instead of running straight off. Drag a finger across the back of the spoon — if the line holds and does not close up, it is thick enough.',
    },
    {
      term: 'shimmer',
      plain:
        'Hot oil develops faint ripples that move across the surface just before it starts to smoke. That is the moment to add the meat. Oil that is not shimmering yet will steam your beef instead of searing it.',
    },
  ],

  inspiration: [
    {
      label: "Damn Delicious — P.F. Chang's Mongolian Beef Copycat",
      url: 'https://damndelicious.net/2013/10/05/pf-changs-mongolian-beef-copycat-recipe/',
    },
    { label: 'FreezerFit — freezer-meal version', url: 'https://freezerfit.com/copycat-pf-changs-mongolian-beef/' },
  ],

  whereWeVeered:
    'The original shallow-fries the beef in a full cup of oil. We sear it in batches in about ¼ cup instead — cleaner, cheaper, and the difference in the finished dish is small once it has been glazed and frozen. We also grate a carrot into the sauce, which vanishes entirely and adds a vegetable, skip the chili flakes so it stays mild, and freeze it without rice so nothing turns to paste on reheat.',
};
